import { useCallback, useEffect, useRef, useState } from 'react'
import { useSnackbar } from 'notistack'
import {
  portalPageCreateService,
  portalPageDeleteService,
  portalPageUpdateService,
  shopLandingPageGetService,
  shopLandingPageUpdateService,
} from 'src/api/collection'
import { errorMessage } from './parts'
import safeTrack from 'src/utils/safe-track'
import trackPortalChanges from './trackPortalChanges'

// Fields PUT /ShopLandingPage takes; pages change through their own calls.
const SETTINGS_FIELDS = [
  'preset',
  'menuItems',
  'showSignIn',
  'headerBackgroundColor',
  'headerTextColor',
  'stickyHeader',
  'showHeader',
  'showFooter',
  'showBreadcrumbs',
  'headerLogo',
  'headerLogoSize',
  'headerFont',
  'headerTextSize',
  'footerLinks',
  'socialLinks',
  'footerBackgroundColor',
  'footerTextColor',
  'footerLogo',
  'footerLogoSize',
  'footerFont',
  'footerTextSize',
  'copyright',
]

const pick = portal =>
  Object.fromEntries(SETTINGS_FIELDS.map(key => [key, portal[key]]))

/**
 * The workspace portal (menu, header, footer, SEO, pages), loaded once
 * `enabled` turns true. Only Navigation and Footer use it; Landing page and
 * SEO read the showcase (home) landing page.
 * - update(fn, message): applies fn(portal) right away and saves the settings;
 *   a failed save rolls back. Saves run one at a time, so quick changes
 *   (toggling twice) reach the server in order and the last one wins.
 * - createPage / updatePage / deletePage: resolve to the saved portal (or
 *   null on failure, after showing the error).
 */
const useShopLandingPage = enabled => {
  const { enqueueSnackbar } = useSnackbar()
  const [portal, setPortal] = useState(null)
  const savedRef = useRef(null)
  // The portal as last confirmed by the server, to roll back to.
  const confirmedRef = useRef(null)
  const queueRef = useRef(Promise.resolve())

  const apply = useCallback(next => {
    savedRef.current = next
    setPortal(next)
    return next
  }, [])

  /** Applies a portal the server returned. */
  const confirm = useCallback(
    next => {
      confirmedRef.current = next
      return apply(next)
    },
    [apply],
  )

  useEffect(() => {
    if (!enabled || savedRef.current) return undefined
    let cancelled = false
    shopLandingPageGetService()
      .then(response => {
        if (cancelled) return
        confirmedRef.current = response?.data || null
        apply(response?.data || null)
      })
      .catch(error => {
        if (!cancelled)
          enqueueSnackbar(errorMessage(error, 'Could not load the portal.'), {
            variant: 'error',
          })
      })
    return () => {
      cancelled = true
    }
  }, [enabled, apply, enqueueSnackbar])

  const fail = useCallback(
    (error, fallback) => {
      enqueueSnackbar(errorMessage(error, fallback), { variant: 'error' })
      return null
    },
    [enqueueSnackbar],
  )

  const update = useCallback(
    (fn, message = 'Saved') => {
      const current = savedRef.current
      if (!current) return Promise.resolve(null)
      const next = apply(fn(current))
      const save = () => {
        // A newer change is already waiting and saves everything; skip this.
        if (savedRef.current !== next) return next
        const before = confirmedRef.current
        return shopLandingPageUpdateService(pick(next))
          .then(response => {
            confirmedRef.current = response?.data || next
            trackPortalChanges(before, confirmedRef.current)
            if (savedRef.current === next && response?.data)
              apply(response.data)
            if (message) enqueueSnackbar(message, { variant: 'success' })
            return response?.data || next
          })
          .catch(error => {
            if (savedRef.current === next && confirmedRef.current)
              apply(confirmedRef.current)
            return fail(error, 'Could not save.')
          })
      }
      const result = queueRef.current.then(save)
      queueRef.current = result.catch(() => null)
      return result
    },
    [apply, enqueueSnackbar, fail],
  )

  const createPage = useCallback(
    data =>
      portalPageCreateService(data)
        .then(response => {
          safeTrack('created_portal_page', {
            show_in_menu: Boolean(data?.showInMenu),
          })
          return confirm(response.data)
        })
        .catch(error => fail(error, 'Could not create the page.')),
    [confirm, fail],
  )

  const updatePage = useCallback(
    (id, data) =>
      portalPageUpdateService(id, data)
        .then(response => {
          enqueueSnackbar('Saved', { variant: 'success' })
          return confirm(response.data)
        })
        .catch(error => fail(error, 'Could not save the page.')),
    [confirm, enqueueSnackbar, fail],
  )

  const deletePage = useCallback(
    id =>
      portalPageDeleteService(id)
        .then(response => {
          safeTrack('deleted_portal_page')
          enqueueSnackbar('Page removed', { variant: 'success' })
          return confirm(response.data)
        })
        .catch(error => fail(error, 'Could not remove the page.')),
    [confirm, enqueueSnackbar, fail],
  )

  return { portal, update, createPage, updatePage, deletePage }
}

export default useShopLandingPage
